/**
 * TRTF localization form
 * ------------------------------------------------------------------
 * The theme ships the markup for a header country/currency selector - a
 * disclosure button, a list of every market, and a hidden country_code input
 * inside a Shopify localization form - but it never ships the script that
 * drives it. <localization-form> was left undefined, so the button did nothing
 * and the country links were dead href="#" anchors. That is almost certainly
 * why the selector was switched off in the header settings to begin with.
 *
 * This supplies the missing behaviour and nothing more:
 *   - the button opens and closes the list
 *   - picking a country writes its code into the form and submits it, which is
 *     how Shopify switches market, and therefore currency
 *   - an outside click, Escape, or the button itself closes it again
 *
 * The same element also serves the language selector further down the snippet,
 * which has the identical shape with a locale_code input instead.
 */
(function () {
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.disclosure__button');
      this.list = this.querySelector('.disclosure__list');
      this.input = this.querySelector(
        'input[name="country_code"], input[name="locale_code"]'
      );
      this.form = this.querySelector('form');
      if (!this.button || !this.list || !this.input || !this.form) return;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.button.addEventListener('click', this.toggle.bind(this));
      this.list.addEventListener('click', this.onPick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    get open() {
      return this.button.getAttribute('aria-expanded') === 'true';
    }

    set open(value) {
      this.button.setAttribute('aria-expanded', value ? 'true' : 'false');
      this.list.toggleAttribute('hidden', !value);
      if (value) {
        document.addEventListener('click', this.onDocumentClick);
      } else {
        document.removeEventListener('click', this.onDocumentClick);
      }
    }

    toggle(event) {
      event.preventDefault();
      // Without this the same click reaches the document handler below and
      // closes the list in the same tick it was opened.
      event.stopPropagation();
      this.open = !this.open;
    }

    onPick(event) {
      const link = event.target.closest('[data-value]');
      if (!link) return;
      event.preventDefault();
      this.input.value = link.dataset.value;
      this.form.submit();
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.open = false;
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      this.open = false;
      this.button.focus();
    }
  }

  if (!customElements.get('localization-form')) {
    customElements.define('localization-form', LocalizationForm);
  }
})();
